import React, { useEffect, useReducer } from 'react'

import { MobileView } from "react-device-detect"

import './css/Dialog.scss'
import { MainView } from './MainView'

import { useBlockingApi } from '../hooks/useBlockingApi'
import { useLoadingOverlay } from '../hooks/useLoadingOverlay'
import { NotifyContext, useNotify } from '../hooks/useNotify'
import { partNeedsQuote, partReadyForQuote, useQuote } from '../hooks/useQuote'
import { useUploadQueue } from '../hooks/useUploadQueue'

import { useHistory, useRouteMatch } from 'react-router-dom'

import { Arcified, ArcifiedTube, Customer, CustomerUser, fetchPost, MaterialSheet, MaterialTube, MaterialType, OrderPart, Powder, Setting, Tap, V3Response } from '@oshcut/oshlib'
import { reducer } from '../reducer'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import './css/App.scss'
import './css/fonts.scss'

import { fixUpPart } from 'fixUpPart'
import { ErrorBoundary } from 'react-error-boundary'
import { ParsedOrder, PartType, StateType } from 'types'
import { toolingPromise } from '../getBrakeTooling'
import { useDfmChecks } from '../hooks/useDfmChecks'
import { joinWithMaterialTypes } from '../joinMaterials'
import Log from '../logs'
import { FallbackErrorComponent } from './FallbackErrorComponent'
import LandingMessage from './LandingMessage'
import { RefreshPrompt } from './RefreshPrompt'
import SiteHeader from "./SiteHeader"
import { Dialog } from '@oshcut/components'
import { useAlert } from 'hooks/useAlert'
import SignInForm from './SignInForm'
import SignUpForm from './SignUpForm'
import ForgotPasswordForm from './ForgotPasswordForm'

let key = 'pk_live_FWbuGoKAts01e1EIglKH6sdq'
if (window.location.href.indexOf('localhost') >= 0
  || window.location.href.indexOf('dev3.oshcut.com') >= 0
  || window.location.href.indexOf('sand.oshcut.com') >= 0) {
  key = 'pk_test_Yntq8iG12Ms1xw6FuvfdHqx4'
  Log.info('Using test API key.')
}

const stripePromise = loadStripe(key)

function App() {

  const initialState: StateType = {
    isSignInDialogOpen: false,
    isSignUpDialogOpen: false,
    isForgotPasswordDialogOpen: false,
    customer: null,
    customerUser: null,
    hasAttemptedLogin: true,
    loadingOrder: null,
    loaded: false,
    parts: [],
    order: null,
    quote: null,
    submittedOrder: null,
    order_parts: [],
    selectedPartIds: [],
    selectedContours: [],
    isErrorPanelVisible: false,
    expandedPartInfoKeys: ['generic-part-properties'],
    hoverPartInfoKey: null,
    recentMaterialIds: [],
    recentMaterialTubeIds: [],
    quoteInvalidationTimestamp: 0,
    selectedShipping: null,
    materialSheets: [],
    materialTypes: [],
    materialTubes: [],
    powders: [],
    messages: [],
    selectedLead: null,
    billingEqualsShipping: false,
    linkAnimatorMessages: {
      account: null,
      currentOrder: null
    },
    colorCodeBends: false,
    showBendAllowance: false,
    disableBendTransforms: false,
    showStacked: false,
    bendAdvancedExpanded: false,
    showConvexGroups: false,
    percentBendTransform: 100,
    isOrderLoading: false,
    nestResult: null,
    percentBendTransformById: {},
    previewSelectedContourProperties: {},
    taps: [],
    alertPoints: [],
    readonly: false,
    showTubeDebugHints: false,
  }

  const history = useHistory()

  const [state, dispatch] = useReducer(reducer, initialState)

  const [notify, notificationContent] = useNotify()
  const [apiContent, executeApi, cancelApi] = useBlockingApi()
  const [overlayContent, showLoadingOverlay, hideLoadingOverlay, setProgress] = useLoadingOverlay()

  const { enqueueItem } = useUploadQueue({ parts: state.parts, parentDispatch: dispatch })

  useDfmChecks({ state, dispatch })

  /** Obtain quote */

  // Skip deleted parts, parts with hard errors, and parts without units or material
  let partsNeedingQuote = state.parts.filter(partNeedsQuote)

  // All remaining parts must have READY status. If not, pass null for the quote request
  let isReadyToQuote = partsNeedingQuote.length > 0
    && !partsNeedingQuote.some(p => !partReadyForQuote(p))
    && !!state.order?.guid

  let rfq = state.order && { order_guid: state.order.guid, orderGuid: state.order.guid }
  const [quote] = useQuote(state.quoteInvalidationTimestamp, isReadyToQuote ? rfq : undefined)

  useEffect(() => {
    dispatch({ type: 'ACTION_SET_QUOTE', quote })
  }, [quote])

  // MUST include all routes present in MainView.jsx that contain the orderId
  const _cartUrlOrderId = useRouteMatch<{ orderId?: string }>('/cart/:orderId?/:partId?')?.params.orderId
  const _checkoutUrlOrderId = useRouteMatch<{ orderId?: string }>('/checkout/:orderId')?.params.orderId

  /** Current order ID in the URL, if it exists. */
  const urlOrderId = _cartUrlOrderId || _checkoutUrlOrderId || null



  // when `state.loadingOrder` (a GUID) changes, load the details for that order,
  // dispatch necessary events and set session storage's orderId.
  useEffect(() => {

    async function loadOrder(orderId: string) {
      Log.info('Loading order with ID ' + orderId)

      dispatch({ type: 'ACTION_ORDER_LOADING' })

      let orderResponse = await executeApi<{ order: ParsedOrder }>('/api/v2/order/get', { guid: orderId }, {
        reason: 'Loading order...',
        timeout: 20000,
        timeoutMessage: 'Timed-out while attempting to load order.  Please try again.',
        errorMessage: 'Unable to load order.  ',
        hideOverlay: true
      })

      if ('error' in orderResponse) {
        if (/Unknown order/.test(orderResponse.error)) {
          // Delete order from sessionStorage, and redirect
          sessionStorage.removeItem('orderId')
          history.push('/cart')
        }
        hideLoadingOverlay()
        dispatch({ type: 'ACTION_FINISHED_LOADING' })
        dispatch({ type: 'ACTION_ORDER_LOADED', order: null })
        return
      }

      Log.info('Loaded order: ', orderResponse.order)
      dispatch({ type: 'ACTION_ORDER_LOADED', order: orderResponse.order })

      if (orderResponse.order.status !== 'draft') {
        hideLoadingOverlay()
        Log.info('App attempted to load submitted order.')
        Log.info('Clearing state and changing URL')
        history.push('/cart')
        sessionStorage.removeItem('orderId')
        dispatch({ type: 'ACTION_CLEAR_CART' })
        dispatch({ type: 'ACTION_FINISHED_LOADING' })
        dispatch({ type: 'ACTION_ORDER_LOADED', order: null })
        return
      }

      let orderPartResponse = await executeApi<{ orderParts: OrderPart[] }>('/api/v2/order_part/for_order', { order_guid: orderId }, {
        reason: 'Loading order...',
        timeout: 20000,
        timeoutMessage: 'Timed-out while attempting to load order.  Please try again.',
        errorMessage: 'Unable to load order.  ',
        hideOverlay: true
      })

      if ('error' in orderPartResponse) {
        hideLoadingOverlay()
        return
      }

      dispatch({ type: 'ACTION_ORDER_PARTS_LOADED', order_parts: orderPartResponse.orderParts })
      Log.info('Loaded order_parts: ', orderPartResponse)

      // Probably safe to clear out all other parts now
      dispatch({ type: 'ACTION_SET_SELECTED_PARTS', partIds: [] })
      dispatch({ type: 'ACTION_REMOVE_ALL_PARTS' })

      // Brake tooling must load before parts 
      await toolingPromise

      // Add parts now
      async function createAndLoadPart(order_part: OrderPart) {

        // Add part to our state
        let newPart: PartType = {
          id: order_part.part_id,
          name: order_part.name,
          arcified: undefined,
          status: 'PART_STATUS_LOADING', // Could create additional statuses, too
          displayOrder: order_part.display_order,
          quantity: order_part.qty,
          quantityValid: true,
          dfmStatus: 'DFM_STATUS_INVALID'
        }
        dispatch({ type: 'ACTION_CREATE_PART', part: newPart })

        Log.info('Loading part ' + order_part.name)

        let arcified = await executeApi<Arcified | ArcifiedTube>('/api/v2/arcify/get',
          {
            partId: order_part.part_id,
            version: 'latest',
            type: 'json'
          },
          {
            reason: 'Loading order...',
            timeout: 20000,
            timeoutMessage: 'Timed-out while attempting to load order.  Please try again.',
            errorMessage: (serverMessage) => {
              if (/ECONNREFUSED/.test(serverMessage)) {
                return <span>Part <b>{order_part.name}</b> could not be loaded. Please try again later. (Reason: could not connect to Arcify server.)</span>
              } else if (/status code 404/.test(serverMessage)) {
                return <span>Part <b>{order_part.name}</b> could not be loaded. (Reason: part not found.)</span>
              } else {
                return 'Unable to load order. ' + serverMessage
              }
            },
            hideOverlay: true
          })

        if ('error' in arcified) {
          Log.info(arcified.error)
          hideLoadingOverlay()
          dispatch({ type: 'ACTION_SET_PART_STATUS', partId: order_part.part_id, status: 'PART_STATUS_LOADING_ERROR' })
          return
        }

        // If the part requires any fixup operations, perform them now.
        try {
          arcified = await fixUpPart(arcified)
        } catch (ex) {
            // Ok no worries. It's not absolutely required to reprocess.
          Log.error(ex)
        }

        dispatch({ type: 'ACTION_ARCIFIED_LOADED', partId: order_part.part_id, arcified })
        dispatch({ type: 'ACTION_SET_PART_STATUS', partId: order_part.part_id, status: 'PART_STATUS_READY', dfmStatus: 'DFM_STATUS_INVALID' })
      }

      let i = 0
      for (let orderPart of orderPartResponse.orderParts) {
        await createAndLoadPart(orderPart)
        i++
        let pct = i / orderPartResponse.orderParts.length * 100
        setProgress(pct)
      }

      dispatch({ type: 'ACTION_FINISHED_LOADING' })
      dispatch({ type: 'ACTION_RELOAD_ORDER', orderId: null })
      dispatch({ type: 'ACTION_INVALIDATE_QUOTE' })
      sessionStorage.setItem('orderId', orderId)
      hideLoadingOverlay()
    }

    Log.info('Checking loading state')
    if (state.loadingOrder !== null) {
      Log.info('Calling loadOrder')
      loadOrder(state.loadingOrder)
    }
  }, [state.loadingOrder])


  // If we have an order ID, identified either in the URL or in a session variable,
  // load that order if it doesn't match the ID of the order in state.
  // Also, if the URL does not contain the order ID and we are at /cart, 
  // change the URL to include the cart ID.
  useEffect(() => {
    // Check auth before attempting to load order
    if (!state.hasAttemptedLogin) {

      let ssOrderId = sessionStorage.getItem('orderId')

      let orderId
      if (urlOrderId) {
        orderId = urlOrderId
      }
      else if (ssOrderId) {
        orderId = ssOrderId
      }

      if (orderId) {
        if (orderId !== state?.order?.guid && !state.loadingOrder) {
          Log.info('LOADING ORDER')
          showLoadingOverlay('Loading order...')
          dispatch({ type: 'ACTION_RELOAD_ORDER', orderId: orderId })
        }

        // If the url does not contain the order ID, and we are viewing the cart, change the URL to include the cart ID.
        if (!urlOrderId && window.location.pathname.indexOf('/cart') === 0) {
          const regexPartIdMatcher = /P-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}/
          let urlPartId = window.location.pathname.match(regexPartIdMatcher)
          Log.info('Replacing url to match order')
          if (urlPartId) {
            history.replace(`/cart/${encodeURIComponent(orderId)}/${encodeURIComponent(urlPartId[0])}`)
          }
          else {
            history.replace(`/cart/${encodeURIComponent(orderId)}`)
          }
        }

      }
      // No cart ID, set loaded indicator
      else {
        Log.info('NO ID, NO NEED TO LOAD')
        dispatch({ type: 'ACTION_FINISHED_LOADING' })
      }
    }
  }, [state, urlOrderId])

  // Restore data from session storage
  useEffect(() => {
    let ssSubmitedOrder = sessionStorage.getItem('submittedOrder')
    if (ssSubmitedOrder) {
      dispatch({ type: 'ACTION_SET_SUBMITTED_ORDER', order: JSON.parse(ssSubmitedOrder) })
    }
  }, [])

  // Silently "claim" this order on auth
  useEffect(() => {
    async function claim() {
      let result
      try {
        result = await fetchPost('/api/v2/order/claim', { guid: urlOrderId })
      }
      catch (e) {
        Log.error(e)
      }
      Log.info(result)
    }

    if (state.customerUser && urlOrderId) {
      claim()
    }
  }, [state.customerUser, urlOrderId])

  /** Load materials, powders, messages, taps */
  // TODO: This is called by the catalog view as well as the app. Maybe we could fix that.
  useEffect(() => {
    async function loadMaterials() {

      let [
        materialTypesResult,
        materialSheetsResult,
        materialTubeResult,
        messages,
        powdersResult,
        tapsResult,
      ] = await Promise.all([
        fetchPost<V3Response>('/api/v3/material/all'),
        fetchPost<V3Response>('/api/v3/material_sheet/all'),
        fetchPost<V3Response>('/api/v3/material_tube/all'),
        fetchPost<Setting[]>('/api/v2/messages'),
        fetchPost<V3Response>('/api/v3/powder/all'),
        fetchPost<V3Response>('/api/v3/taps/all')
      ])

      let materialTypes = materialTypesResult.result[0].data as MaterialType[]
      let materialSheets = materialSheetsResult.result[0].data as MaterialSheet[]
      let materialTube = materialTubeResult.result[0].data as MaterialTube[]
      let powders = powdersResult.result[0].data as Powder[]
      let taps = tapsResult.result[0].data as Tap[]

      let materials = joinWithMaterialTypes(materialSheets, materialTypes)
      let materialTubes = joinWithMaterialTypes(materialTube, materialTypes)

      materials.sort((a, b) => a.thickness - b.thickness)

      powders = powders.filter(p => p.enabled)

      dispatch({ type: 'ACTION_ALL_MATERIALS_LOADED', materialTubes, materialTypes, materialSheets })
      dispatch({ type: 'ACTION_MESSAGES_LOADED', messages })
      dispatch({ type: 'ACTION_ALL_POWDERS_LOADED', powders })
      dispatch({ type: 'ACTION_ALL_TAPS_LOADED', taps })
    }

    loadMaterials()
  }, [])

  /** Is there a valid customer user cookie? See if logging in works. If so, login the user. */
  useEffect(() => {
    // Remove the old session storage token if it still exists
    sessionStorage.removeItem('token')
    async function checkCustomerUserCookie() {
      try {
        let response = await fetchPost<V3Response>('/api/v3/customer_user/get_by_token')
        let customerUser = response.result[0].data[0] as CustomerUser
        if (customerUser) {
          delete customerUser.token
          dispatch({ type: 'ACTION_USER_IS_LOGGED_IN', customerUser })
        }
      } catch (ex) {
      } finally {
        dispatch({ type: 'ACTION_ATTEMPTED_LOGIN', value: false })
      }
    }
    checkCustomerUserCookie()
  }, [])

  /** Load customer info for the logged in customer_user */
  useEffect(() => {
    if (!state.customerUser) {
      dispatch({ type: 'ACTION_SET_CUSTOMER', customer: null })
      return
    }
    async function loadCustomer() {
      try {
        let customer = await fetchPost<Customer>('/api/v2/customer/get')
        if (customer) {
          dispatch({ type: 'ACTION_SET_CUSTOMER', customer })
        }
      } catch (e) { }
    }
    loadCustomer()
  }, [state.customerUser])


  function onError(error: Error, info: any) {
    if (process.env.NODE_ENV !== 'development') {
      fetchPost('/api/v2/status', {
        type: 'error',
        title: error.message,
        details: JSON.stringify({
          message: error.message,
          stack: error.stack,
          info,
          url: window.location.href
        }),
        order_guid: state.order?.guid
      })
        .catch(() => { })
    }
  }


  const [alertContent, showAlert] = useAlert()
  function handleRegister(customerUser: CustomerUser) {
    if (customerUser) {
      showAlert('Welcome to OSH Cut!', 'We created a new account for you at ' + customerUser.email)
      dispatch({ type: 'ACTION_USER_IS_LOGGED_IN', customerUser })
    }
  }

  function handleForgotPasswordSuccess(email: string) {
    showAlert('Email Sent', <div>We sent an email to <b>{email}</b> with instructions for resetting your password. Please check your email to continue.</div>)
    dispatch({ type: 'ACTION_CLOSE_FORGOT_PASSWORD_DIALOG' })
  }

  return (

    <div className="App">
      <ErrorBoundary FallbackComponent={() => <FallbackErrorComponent state={state} dispatch={dispatch} />} onError={onError}>
        <SiteHeader state={state} dispatch={dispatch} />
        <NotifyContext.Provider value={{ notify }}>
          {state.loaded && (<Elements stripe={stripePromise}>
            <MobileView><div className="mobileError">Our web app is best used on a desktop or laptop computer.  It may be difficult to use on a mobile device with a small screen.</div></MobileView>
            <LandingMessage messages={state.messages} />
            <MainView
              state={state}
              dispatch={dispatch}
              enqueueUploadItem={enqueueItem} />
          </Elements>)}
        </NotifyContext.Provider>

        {state.isSignInDialogOpen && (
          <Dialog open title="Sign In" onClose={() => dispatch({ type: 'ACTION_CLOSE_SIGNIN_DIALOG'})}>
            <SignInForm state={state} dispatch={dispatch} onLogin={cu => dispatch({ type: 'ACTION_USER_IS_LOGGED_IN', customerUser: cu })} />
          </Dialog>
        )}

        {state.isSignUpDialogOpen && (
          <Dialog open title="Create Account" onClose={() => dispatch({ type: 'ACTION_CLOSE_SIGNUP_DIALOG'})}>
            <SignUpForm state={state} dispatch={dispatch} onRegister={handleRegister} />
          </Dialog>
        )}

        {state.isForgotPasswordDialogOpen && (<Dialog open title="Forgot Password" onClose={() => dispatch({ type: 'ACTION_CLOSE_FORGOT_PASSWORD_DIALOG'})}>
          <ForgotPasswordForm onSuccess={handleForgotPasswordSuccess} onBack={() => {
            dispatch({ type: 'ACTION_CLOSE_FORGOT_PASSWORD_DIALOG'})
            dispatch({ type: 'ACTION_OPEN_SIGNIN_DIALOG' })
          }} />
        </Dialog>)}

        {notificationContent}
        {apiContent}
        {overlayContent}
        <RefreshPrompt app='customer' />
      </ErrorBoundary>

      {alertContent}
    </div>
  )
}


export default App
